import {
  ATTENDANCE_SETTING_KEY,
  REMINDERS_SETTING_KEY,
  REQUEST_SETTING_KEY,
  readAttendanceSettings,
  readReminders,
  readRequestSettings,
  type AttendanceSettings,
  type RequestSettings,
  type RecordDto,
  type Reminder,
} from '@halo/shared';
import { BRANCHES_KEY, parseBranches, serializeBranches, type Branch } from '../branches';
import { DEFAULT_COMPETITOR_RPM } from '../competitor-research';
import { parseProductionStages, type ProductionStageDef } from '../production';
import { readNumber, readStringList, type RecordData } from './parsing';

/** Cài đặt chung của công ty — MỘT bản ghi `setting`, chỉ Giám đốc ghi được. */
export interface CompanySettings {
  /** id bản ghi; `undefined` khi chưa từng lưu cài đặt nào. */
  recordId: number | undefined;
  /** Ngưỡng CTR %: ô ≥ ngưỡng được tô nổi. */
  ctrThreshold: number;
  /** Ngưỡng RET 45s %: ô < ngưỡng bị tô cảnh báo. */
  retentionThreshold: number;
  /** Ngưỡng RPM (USD): ô ≥ ngưỡng được tô nổi. */
  rpmThreshold: number;
  /**
   * RPM (USD / 1.000 view) dùng để ƯỚC TÍNH doanh thu kênh ĐỐI THỦ. Khác `rpmThreshold` — cái kia
   * là ngưỡng bôi màu cho số thật của kênh nhà; cái này là một phỏng đoán để so đối thủ với nhau.
   */
  competitorRpm: number;
  /** Đơn vị (phòng/nhóm) đầu mối CID. */
  cidUnits: string[];
  /** Danh sách CMS hợp lệ để gán cho kênh. */
  cmsList: string[];
  /** Hình thức thanh toán của hợp đồng đối tác — gợi ý cho ô cùng tên ở hộp sửa kênh. */
  paymentMethods: string[];
  /** ĐỐI TÁC giữ kênh (thêm 29/09/2026) — gợi ý cho ô «Đối tác» ở hộp sửa kênh, và để lọc kênh. */
  partners: string[];
  /** Đơn vị làm nhạc — người trong đó ghi được bảng «KPI Producer». */
  producerUnits: string[];
  /** Tên đăng nhập được tích riêng cho vào nhóm Producer (ngoài chức vụ và đơn vị làm nhạc). */
  productionUsers: string[];
  /** Người được mở mục «Lưu trữ» (mail · mật khẩu · 2FA của kênh) — tích đích danh, xem `canAccessVault`. */
  vaultUsers: string[];
  /** Người được CẤP KEY phần mềm BOLERO RENDER (29/09/2026) — tích đích danh, xem `canIssueBoleroKeys`. */
  boleroUsers: string[];
  /** Người ĐĂNG TIN trên Bảng tin (03/10/2026) — tích đích danh, xem `canPostNews`. */
  newsPosters: string[];
  /** LỊCH NHẮC cả công ty (03/10/2026): 9h30 báo cáo, 10h chỉ số… — luật ở `reminders.ts` (gói shared). */
  reminders: Reminder[];
  /** GIỜ CHẤM CÔNG (05/10/2026): các mốc chấm công, phút cho phép, người được miễn — luật ở `attendance.ts`. */
  attendance: AttendanceSettings;
  /** PHIẾU ĐỀ XUẤT (05/10/2026): phòng hành chính, phòng kế toán, tên / địa chỉ công ty in trên phiếu. */
  requests: RequestSettings;
  /** Loại công việc của Producer, cho ô «Công việc» trong bảng KPI. */
  producerJobs: string[];
  /** Công đoạn của quy trình sản xuất nhạc, theo đúng thứ tự Giám đốc xếp. */
  productionStages: ProductionStageDef[];
  /**
   * Phòng thuộc chi nhánh khác (bị loại khỏi Hồ sơ nhân sự). `undefined` = CHƯA KHAI → máy chủ
   * dùng mặc định; mảng rỗng = «không ẩn phòng nào». Hai chuyện đó khác nhau.
   */
  profileHiddenDepartments: string[] | undefined;
  /** Mã phòng điều hành chung (Trưởng phòng đứng dưới Giám đốc trên sơ đồ công ty); '' = chưa chọn. */
  executiveDepartmentId: string;
  /** Chi nhánh và các phòng thuộc về nó — chỉ để nhìn và lọc (xem `domain/branches.ts`). */
  branches: Branch[];
  /** Dữ liệu gốc — giữ lại để khi ghi không làm rơi các khoá giao diện không biết tới. */
  raw: RecordData;
}

export const DEFAULT_THRESHOLDS = { ctr: 7, retention: 50, rpm: 5 } as const;

export function parseCompanySettings(record: RecordDto | undefined): CompanySettings {
  const data = record?.data ?? {};
  return {
    recordId: record?.id,
    ctrThreshold: readNumber(data, 'ctr') ?? DEFAULT_THRESHOLDS.ctr,
    retentionThreshold: readNumber(data, 'ret') ?? DEFAULT_THRESHOLDS.retention,
    rpmThreshold: readNumber(data, 'rpm') ?? DEFAULT_THRESHOLDS.rpm,
    competitorRpm: readNumber(data, 'rpmDoiThu') ?? DEFAULT_COMPETITOR_RPM,
    cidUnits: readStringList(data, 'cidUnits'),
    cmsList: readStringList(data, 'cmsList'),
    paymentMethods: readStringList(data, 'httt'),
    partners: readStringList(data, 'doitac'),
    producerUnits: readStringList(data, 'sxUnits'),
    productionUsers: readStringList(data, 'sxUsers'),
    vaultUsers: readStringList(data, 'luutruUsers'),
    boleroUsers: readStringList(data, 'boleroUsers'),
    newsPosters: readStringList(data, 'bangtinUsers'),
    reminders: readReminders(data[REMINDERS_SETTING_KEY]),
    attendance: readAttendanceSettings(data[ATTENDANCE_SETTING_KEY]),
    requests: readRequestSettings(data[REQUEST_SETTING_KEY]),
    producerJobs: readStringList(data, 'sxJobs'),
    productionStages: parseProductionStages(data.sxStages),
    profileHiddenDepartments: Array.isArray(data.hosoAnPhong)
      ? readStringList(data, 'hosoAnPhong')
      : undefined,
    executiveDepartmentId: typeof data.execDept === 'string' ? data.execDept.trim() : '',
    branches: parseBranches(data[BRANCHES_KEY]),
    raw: data,
  };
}

/** Chỉ gửi các khoá ĐỔI; máy chủ tự trộn với phần đang lưu (không ghi đè nguyên bản ghi). */
export function serializeSettingsChange(
  change: Partial<Omit<CompanySettings, 'recordId' | 'raw'>>,
): RecordData {
  const data: RecordData = {};
  if (change.ctrThreshold !== undefined) data.ctr = change.ctrThreshold;
  if (change.retentionThreshold !== undefined) data.ret = change.retentionThreshold;
  if (change.rpmThreshold !== undefined) data.rpm = change.rpmThreshold;
  if (change.competitorRpm !== undefined) data.rpmDoiThu = change.competitorRpm;
  if (change.cidUnits !== undefined) data.cidUnits = change.cidUnits;
  if (change.cmsList !== undefined) data.cmsList = change.cmsList;
  if (change.paymentMethods !== undefined) data.httt = change.paymentMethods;
  if (change.partners !== undefined) data.doitac = change.partners;
  if (change.producerUnits !== undefined) data.sxUnits = change.producerUnits;
  if (change.productionUsers !== undefined) data.sxUsers = change.productionUsers;
  if (change.vaultUsers !== undefined) data.luutruUsers = change.vaultUsers;
  if (change.boleroUsers !== undefined) data.boleroUsers = change.boleroUsers;
  if (change.newsPosters !== undefined) data.bangtinUsers = change.newsPosters;
  if (change.reminders !== undefined) data[REMINDERS_SETTING_KEY] = change.reminders;
  if (change.attendance !== undefined) data[ATTENDANCE_SETTING_KEY] = change.attendance;
  if (change.requests !== undefined) data[REQUEST_SETTING_KEY] = change.requests;
  if (change.producerJobs !== undefined) data.sxJobs = change.producerJobs;
  if (change.productionStages !== undefined) data.sxStages = change.productionStages;
  if (change.profileHiddenDepartments !== undefined) {
    data.hosoAnPhong = change.profileHiddenDepartments;
  }
  if (change.executiveDepartmentId !== undefined) data.execDept = change.executiveDepartmentId;
  if (change.branches !== undefined) data[BRANCHES_KEY] = serializeBranches(change.branches);
  return data;
}
